import { Injectable, inject } from '@angular/core';
import { TestDto } from '@core/services/test/test-dto';
import { ChartMappingData } from '@modules/apc/apc/data/chart-mapping-data';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { ScalingFactor } from '@modules/report-generator/constants';
import { ReportConfig } from '@modules/report-generator/interfaces';
import { X_AXIS_TYPE } from '@shared/enums/x-axis-type.enum';
import * as _ from 'lodash-es';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import { HelperService } from '@shared/services/helper.service';
import { randomSuffix } from '@modules/report-generator/shared';

declare let Bokeh_v0123: any;

const Defaults = {
  Chart: {
    FullWidth: 1160,
    Height: 450,
    BackgroundFillColor: '#FFF',
    BorderFillColor: '#FFF',
    TitleColor: '#222222',
  },
  Legend: {
    BorderWidth: 0,
    BackgroundFillAlpha: 0.75,
  },
  BorderColor: '#BBBBBB',
  LabelTextColor: '#444444',
  GridLineColor: '#BBBBBB',
};

const titleCase = (str: string) =>
  str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export interface MetabolicPlot4 {
  plots: Array<Record<string, any>>;
}

@Injectable({
  providedIn: 'root',
})
export class PerformanceDevelopmentChartService {
  private readonly customRendererService: CustomRendererService = inject(CustomRendererService);
  private helperService: HelperService = inject(HelperService);
  private plottingAPI = Bokeh_v0123.Plotting;

  destroyChart(elementId: string) {
    this.customRendererService.emptyAndCleanupBokeh(elementId || ChartConstants.BODY_COMPOSITION.ELEMENT_ID);
  }

  renderPerformanceDevelopment(
    metabolicPlot4: MetabolicPlot4,
    test: TestDto,
    htmlElementId?: string,
    scaleText = 1
  ): void {
    const hasPlots = Boolean(
      metabolicPlot4.plots && Array.isArray(metabolicPlot4.plots) && metabolicPlot4.plots.length > 0
    );
    if (!hasPlots) {
      return;
    }

    const plot = metabolicPlot4.plots[0];
    plot.x_axis_type = X_AXIS_TYPE.DATETIME;
    const elementId = htmlElementId || ChartConstants.PERFORMANCE_DEVELOPMENT_3.ELEMENT_ID;
    this.customRendererService.emptyAndCleanupBokeh(elementId);

    metabolicPlot4.plots.forEach((plot, index) => {
      const graphDivId = `performance_development_graph_${index}_${randomSuffix()}`;
      this.customRendererService.emptyAndCleanupBokeh(`#${graphDivId}`);

      const graphNode = this.customRendererService.createNode({
        selectorName: 'div',
        id: graphDivId,
      });

      this.customRendererService.append(elementId, graphNode);

      if (index > 0) {
        plot['title_text'] = '';
      }

      try {
        const payload = {
          plot,
          elementId: `#${graphDivId}`,
          testId: test.id,
          type: ChartConstants.PERFORMANCE_DEVELOPMENT_3.ID,
          legend: 'legends',
          mappingData: ChartMappingData,
        };
        this.generateSeparatePerformanceDevelopment(payload.plot, payload.elementId, payload.testId, scaleText, false);
      } catch (error) {
        console.error('Unable to render the performance development chart', error);
      }
    });
  }

  private generateSeparatePerformanceDevelopment(
    plot: any,
    elementId: any,
    testId: any,
    scaleText = 1,
    isPdf: boolean = false
  ): void {
    if (!plot) {
      return;
    }

    let lineObject: { y_range_name: any } | undefined;
    let fontSizeValue = 9;

    let plotData: any;
    plotData = {
      plot_width: Defaults.Chart.FullWidth,
      plot_height: Defaults.Chart.Height,
      tools: false,
      x_axis_type: plot.x_axis_type,
      background_fill_color: Defaults.Chart.BackgroundFillColor,
      border_fill_color: Defaults.Chart.BorderFillColor,
      outline_line_color: Defaults.BorderColor,
      sizing_mode: 'scale_width',
      toolbar_location: null,
      // min_border_bottom: 30,
    };

    if (plot.x_axis_type === X_AXIS_TYPE.DATETIME) {
      this.handelSeparateXValPerformanceChart(plotData, plot, testId, plot.name, '_PDXsettings_'); // IAT-2354 let x axis auto scaling
      this.handelSeparateYValPerformanceChart(plotData, plot, testId, plot.name, '_PDYsettings_');
    } else {
      this.handelSeparateXValPerformanceChart(plotData, plot, testId, plot.name, '_PDXLinearSettings_'); // IAT-2354 let x axis auto scaling
      this.handelSeparateYValPerformanceChart(plotData, plot, testId, plot.name, '_PDYLinearSettings_');
    }

    if (plot.extra_y_ranges) {
      plotData.extra_y_ranges = this.createExtraRangeForPerformanceDevelopment(plot.extra_y_ranges, testId);
    }

    const y_extra_list: string[] = [];
    for (let key in plot.extra_y_ranges) y_extra_list.push(key.toString());

    // BEFORE creating the chart, ensure plot data is properly configured:
    if (plot.use_custom_x_axis) {
      // Explicitly set x_axis_type to linear
      plotData.x_axis_type = 'linear';

      // Create a proper Range1d object with explicit start/end
      if (plot.x_range && plot.x_range.plot_range) {
        plotData.x_range = new Bokeh_v0123.Range1d({
          start: plot.x_range.plot_range[0],
          end: plot.x_range.plot_range[1],
        });
      } else {
        // Fallback to a safe default range
        plotData.x_range = new Bokeh_v0123.Range1d({
          start: -0.5,
          end: plot.custom_x_ticks && plot.custom_x_ticks.length ? plot.custom_x_ticks.length - 0.5 : 10,
        });
      }
    }

    let chart = this.plottingAPI.figure(plotData);

    //**
    // AFTER creating the chart, set up the custom ticks:
    if (plot.use_custom_x_axis && plot.custom_x_ticks && plot.date_labels) {
      try {
        // Use a simpler ticker implementation
        const ticker = new Bokeh_v0123.FixedTicker({
          ticks: plot.custom_x_ticks,
        });

        // Create a simple mapping formatter
        const labelMap: any = {};
        plot.custom_x_ticks.forEach((tick: any, i: any) => {
          if (i < plot.date_labels.length) {
            labelMap[tick] = plot.date_labels[i];
          }
        });

        const formatter = new Bokeh_v0123.FuncTickFormatter({
          args: { labelMap: labelMap },
          code: "return labelMap[tick] !== undefined ? labelMap[tick] : '';",
        });

        // Set ticker and formatter safely
        if (chart && chart.xaxis) {
          // Set existing properties
          chart.xaxis.ticker = ticker;
          chart.xaxis.formatter = formatter;

          // Calculate appropriate rotation based on number of labels and label length
          const labels = plot.date_labels || [];
          const labelCount = labels.length;
          const avgLabelLength =
            labels.reduce((sum: any, label: any) => sum + String(label).length, 0) / (labelCount || 1);

          let rotation = 0;
          let bottomMargin = 30;
          let textAlign = 'center';
          let textBaseline = 'top';

          // Dynamic rotation calculation
          if (labelCount > 15 || (labelCount > 8 && avgLabelLength > 10)) {
            // Many labels or long labels - vertical
            rotation = Math.PI / 2; // 90 degrees
            bottomMargin = 40;
            textAlign = 'right';
            textBaseline = 'middle';
          } else if (labelCount > 8 || (labelCount > 5 && avgLabelLength > 8)) {
            // Medium number - 45 degrees
            rotation = Math.PI / 4; // 45 degrees
            bottomMargin = 60;
            textAlign = 'right';
            textBaseline = 'middle';
          } else if (labelCount > 3) {
            // Few labels - slight angle
            rotation = Math.PI / 8; // 22.5 degrees
            bottomMargin = 30;
            textAlign = 'right';
            textBaseline = 'top';
          }

          // Apply settings
          chart.xaxis.major_label_orientation = rotation;
          chart.xaxis.major_label_standoff = 5;
          chart.xaxis.major_label_text_align = textAlign;
          chart.xaxis.major_label_text_baseline = textBaseline;
          chart.min_border_bottom = bottomMargin;

          // Additional adjustment for extreme cases
          if (labelCount > 25) {
            // For extreme cases, only show every other label
            const skipTicker = new Bokeh_v0123.FixedTicker({
              ticks: plot.custom_x_ticks.filter((_: any, i: any) => i % 2 === 0),
            });
            chart.xaxis.ticker = skipTicker;
          }
        }
      } catch (error) {
        console.error('Error setting custom axis:', error);
      }
    }
    //

    this.drawPerformanceDevelopment(plot, lineObject, chart, y_extra_list);

    if (plot.layouts && plot.layouts.length > 0) {
      _.each(plot.layouts, (res): void => {
        chart = this.createLayout(res, chart);
      });
    }

    let performanceXCallbackCode: string;

    if (plot.x_axis_type === X_AXIS_TYPE.DATETIME) {
      performanceXCallbackCode = 'let data = this.attributes.args.data; saveSettingsForPD_' + plot.name + '(cb_obj);';
    } else {
      performanceXCallbackCode =
        'let data = this.attributes.args.data; saveSettingsForPDLinear_' + plot.name + '(cb_obj);';
    }

    // //let performanceCallbackCode = 'let data = this.attributes.args.data;';
    chart.x_range.callback = new Bokeh_v0123.CustomJS({
      code: performanceXCallbackCode,
      args: new Bokeh_v0123.ColumnDataSource({
        data: plot,
      }),
    });

    let performanceYCallbackCode: string;

    if (plot.x_axis_type === X_AXIS_TYPE.DATETIME) {
      performanceYCallbackCode = 'let data = this.attributes.args.data; saveYSettingsForPD_' + plot.name + '(cb_obj);';
    } else {
      performanceYCallbackCode =
        'let data = this.attributes.args.data; saveYSettingsForPDLinear_' + plot.name + '(cb_obj);';
    }

    chart.y_range.callback = new Bokeh_v0123.CustomJS({
      code: performanceYCallbackCode,
      args: new Bokeh_v0123.ColumnDataSource({
        data: plot,
      }),
    });

    chart.title.text = titleCase(plot['title_text']);
    chart.title.align = 'center';
    chart.title.text_font_size = `${fontSizeValue + 1}pt`;
    chart.title.text_color = Defaults.Chart.TitleColor;
    // Legend Styling
    chart._legend.location = 'top_left';
    chart._legend.label_text_font_size = `${fontSizeValue}pt`;
    chart._legend.label_height = fontSizeValue;
    chart._legend.label_width = fontSizeValue;
    chart._legend.glyph_height = fontSizeValue;
    chart._legend.glyph_width = fontSizeValue;
    chart._legend.background_fill_color = Defaults.Chart.BackgroundFillColor;
    chart._legend.border_line_width = Defaults.Legend.BorderWidth;
    chart._legend.background_fill_alpha = Defaults.Legend.BackgroundFillAlpha;
    chart._legend.label_text_color = Defaults.LabelTextColor;
    chart.yaxis.axis_label = plot.y_axis_label;
    chart.yaxis.major_label_text_font_size = `${fontSizeValue}pt`;
    chart.yaxis.axis_label_text_font_size = `${fontSizeValue}pt`;
    chart.yaxis.axis_label_text_font_style = 'normal';
    chart.yaxis.axis_label_text_color = Defaults.LabelTextColor;
    chart.yaxis.major_label_text_color = Defaults.LabelTextColor;
    chart.xaxis.major_label_text_font_size = `${fontSizeValue}pt`;
    chart.xaxis.axis_label_text_font_size = `${fontSizeValue}pt`;
    chart.xaxis.axis_label_text_font_style = 'normal';
    chart.xaxis.axis_label_text_color = Defaults.LabelTextColor;
    chart.xaxis.major_label_text_color = Defaults.LabelTextColor;

    if (chart.right) {
      _.each(chart.right, (right): void => {
        right.attributes.major_label_text_font_size = `${fontSizeValue}pt`;
        right.attributes.axis_label_text_font_size = `${fontSizeValue}pt`;
        right.attributes.axis_label_text_font_style = 'normal';
        right.attributes.axis_label_text_color = Defaults.LabelTextColor;
        right.attributes.major_label_text_color = Defaults.LabelTextColor;
      });
    }

    if (chart.left) {
      _.each(chart.left, (left): void => {
        left.attributes.axis_label_text_font_style = 'normal';
        left.attributes.major_label_text_font_size = `${fontSizeValue}pt`;
        left.attributes.axis_label_text_font_size = `${fontSizeValue}pt`;
        left.attributes.axis_label_text_color = Defaults.LabelTextColor;
        left.attributes.major_label_text_color = Defaults.LabelTextColor;
      });
    }

    //remove grid lines in chart
    // Grid Styling
    chart.xgrid.grid_line_color = Defaults.GridLineColor;
    chart.ygrid.grid_line_color = Defaults.GridLineColor;

    this.plottingAPI.show(chart, elementId);
  }

  private drawPerformanceDevelopment(plot: any, lineObject: any, chart: any, y_extra_list: any): void {
    const all_renderer: any[] = [];
    const all_legends: any[] = [];
    let legend, circleObject, source, tooltip, layout;

    if (
      plot.lines &&
      plot.circles &&
      plot.layouts &&
      plot.lines.length > 0 &&
      plot.circles.length > 0 &&
      plot.layouts.length > 0 &&
      plot.extra_tools.length > 0
    ) {
      _.each(plot.lines, (line, index: string): void => {
        if (line.source.x) {
          line.source.x = line.source.x.map((res: string | number | Date) => {
            return new Date(res).getTime();
          });
        }

        source = new Bokeh_v0123.ColumnDataSource({ data: line.source });
        layout = plot.layouts[plot.layouts.length - 1];
        legend = layout.legends[index][0];
        tooltip = plot.extra_tools[index].tooltips;
        lineObject = chart.line({
          x: {
            field: 'x',
          },
          y: {
            field: 'y',
          },
          source: source,
          line_width: line.line_width,
          line_color: line.line_color,
          legend: legend,
        });
        //mysterious here for extra y-axis display
        if (y_extra_list.indexOf(line.y_range_name) != -1) lineObject.y_range_name = line.y_range_name;

        circleObject = chart.circle({
          x: { field: 'x' },
          y: { field: 'y' },
          source: source,
          color: line.line_color,
          fill_color: plot.circles[index].fill_color,
          legend: legend,
          size: plot.circles[index].size,
        });
        if (y_extra_list.indexOf(line.y_range_name) != -1) circleObject.y_range_name = line.y_range_name;

        all_renderer.push(circleObject);
        all_legends.push(
          new Bokeh_v0123.LegendItem({
            label: legend,
            renderers: [lineObject, circleObject],
          })
        );
        chart.add_tools(
          new Bokeh_v0123.HoverTool({
            renderers: [circleObject],
            tooltips: tooltip,
          })
        );
      });
    }
  }

  private handelSeparateXValPerformanceChart(
    plotData: any,
    plot: any,
    testId: any,
    name: string,
    settings: string
  ): void {
    let xVal: any = localStorage.getItem(name + settings + testId);
    if (xVal) {
      xVal = JSON.parse(xVal);
      plotData.x_range = new Bokeh_v0123.Range1d({
        start: new Date(xVal.plot_range[0]).getTime(),
        end: new Date(xVal.plot_range[1]).getTime(),
      });
    } else {
      if (plot.is_one_day) {
        plotData.x_range = new Bokeh_v0123.Range1d({
          start: new Date(plot.x_range.plot_range[0]).getTime(),
          end: new Date(plot.x_range.plot_range[1]).getTime(),
        });
      }
    }
  }

  private handelSeparateYValPerformanceChart(
    plotData: any,
    plot: any,
    testId: any,
    name: string,
    settings: string
  ): void {
    if (plot.y_range) {
      let minMaxYAxis: any;
      // IAT-2354 Use default value [0,100], not depending on plots data
      let xVal: any = localStorage.getItem(name + settings + testId);
      const yAxisRange: any = this.getDefaultYAxisRange(plot);
      let start: number = ChartConstants.RANGE.START;
      let end: number = ChartConstants.RANGE.END;
      if (xVal) {
        xVal = JSON.parse(xVal);
        start = xVal.plot_range[0];
        end = xVal.plot_range[1];
      }

      minMaxYAxis = this.calculateOptimalBounds([yAxisRange.start, yAxisRange.end]);

      plotData.y_range = new Bokeh_v0123.Range1d({
        start: minMaxYAxis.min,
        end: minMaxYAxis.max,
      });
    }
  }

  private getDefaultYAxisRange(plot: any): any {
    let yAxisRange: number[] = [];

    plot.lines.forEach((line: any) => {
      yAxisRange.push(...line.source.y);
    });

    yAxisRange.sort((a: number, b: number) => a - b);

    return {
      start: yAxisRange[0],
      end: yAxisRange[yAxisRange.length - 1],
    };
  }

  public createExtraRangeForPerformanceDevelopment(range: any, testId: any) {
    let extradr: any = {};
    const currentValue: number[] = [];
    for (let range_name in range) {
      if (range.hasOwnProperty(range_name)) {
        extradr[range_name] = new Bokeh_v0123.Range1d({
          start: range[range_name].plot_range[0],
          end: range[range_name].plot_range[1],
          // bounds: range[range_name].bounds,
        });
      }
    }

    return extradr;
  }

  private createLayout(layout: any, chart: any) {
    for (let type in layout) {
      if (type != 'axis_position' && layout.hasOwnProperty(type)) {
        if (layout[type] != null) {
          switch (type) {
            case 'linear_axis':
              chart = this.createLinearAxis(layout[type], chart, layout['axis_position']);
              break;
          }
        }
      }
    }

    return chart;
  }

  private createLinearAxis(
    layout: {
      y_range_name: any;
      axis_label: any;
      axis_label_text_font_size: any;
    },
    chart: { add_layout: (arg0: any, arg1: any) => void },
    position: any
  ) {
    chart.add_layout(
      new Bokeh_v0123.LinearAxis({
        y_range_name: layout.y_range_name,
        axis_label: layout.axis_label,
        axis_label_text_font_size: layout.axis_label_text_font_size,
      }),
      position
    );

    return chart;
  }

  private calculateOptimalBounds(dataArray: any, paddingPercent?: number) {
    paddingPercent = paddingPercent || 10;

    let minValue = Math.min.apply(Math, dataArray);
    let maxValue = Math.max.apply(Math, dataArray);
    let range = maxValue - minValue;
    let padding = (paddingPercent / 100) * range;
    let lowestValue = minValue - padding;
    let highestValue = maxValue + padding;

    if (range < 0.01) {
      padding = Math.abs(minValue * 0.1) || 1; // 10% of value or 1 as fallback
    }

    return {
      min: lowestValue,
      max: highestValue,
      dataMin: minValue,
      dataMax: maxValue,
      range: range,
    };
  }

  private preprocessPlotData(plot: any): any {
    // Create a deep copy of the plot to avoid modifying the original
    const processedPlot = this.helperService.makeCopy(plot);

    // Only process if we have lines and we're dealing with datetime x-axis
    if (
      processedPlot &&
      processedPlot.lines &&
      processedPlot.lines.length > 0 &&
      (processedPlot.x_axis_type === 'datetime' || processedPlot.x_axis_type === 'date_gap')
    ) {
      // Extract all unique dates and their data points
      const uniqueDates = new Map();

      try {
        // Collect all unique dates from all lines
        processedPlot.lines.forEach((line: any) => {
          if (line.source && line.source.x && Array.isArray(line.source.x)) {
            line.source.x.forEach((dateVal: any, index: number) => {
              try {
                const dateObj = new Date(dateVal);

                if (isNaN(dateObj.getTime())) {
                  console.warn(`Invalid date found: ${dateVal}`);
                  return;
                }

                const timestamp = dateObj.getTime();

                // Format date for display
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                const formattedDate = `${day}.${month}.${year}`;

                uniqueDates.set(timestamp, {
                  formatted: formattedDate,
                  originalDate: dateVal,
                });
              } catch (error) {
                console.error(`Error processing date: ${dateVal}`, error);
              }
            });
          }
        });

        if (uniqueDates.size === 0) {
          return processedPlot;
        }

        // Convert map to sorted array
        const sortedTimestamps = Array.from(uniqueDates.keys()).sort((a, b) => a - b);

        // Now let's distribute the points - we'll use simple integer indices
        const positionMap = new Map();
        sortedTimestamps.forEach((timestamp, index) => {
          positionMap.set(timestamp, index);
        });

        // Update source data for each line
        processedPlot.lines.forEach((line: any) => {
          if (line.source && line.source.x && Array.isArray(line.source.x)) {
            line.source.original_x = [...line.source.x];

            // Keep the date in a format that works for tooltips
            if (line.source.date) {
              line.source.date_display = line.source.date;
            }

            // Convert dates to indices
            line.source.x = line.source.x.map((dateVal: any) => {
              try {
                const timestamp = new Date(dateVal).getTime();
                return positionMap.has(timestamp) ? positionMap.get(timestamp) : 0;
              } catch (e) {
                return 0;
              }
            });
          }
        });

        // Same for circles
        if (processedPlot.circles) {
          processedPlot.circles.forEach((circle: any) => {
            if (circle.source && circle.source.x && Array.isArray(circle.source.x)) {
              circle.source.original_x = [...circle.source.x];

              if (circle.source.date) {
                circle.source.date_display = circle.source.date;
              }

              circle.source.x = circle.source.x.map((dateVal: any) => {
                try {
                  const timestamp = new Date(dateVal).getTime();
                  return positionMap.has(timestamp) ? positionMap.get(timestamp) : 0;
                } catch (e) {
                  return 0;
                }
              });
            }
          });
        }

        // Store the x-axis configuration
        processedPlot.custom_x_ticks = Array.from(positionMap.values());
        processedPlot.date_labels = sortedTimestamps.map((ts) => uniqueDates.get(ts).formatted);

        // IMPORTANT: Modify the x-axis type to ensure it works with the data
        processedPlot.original_x_axis_type = processedPlot.x_axis_type;
        processedPlot.x_axis_type = 'linear'; // Use linear instead of null/datetime

        // Set explicit range values - use integers to avoid floating point issues
        const numPoints = processedPlot.custom_x_ticks.length;
        processedPlot.x_range = {
          bounds: ['auto'],
          plot_range: [-0.5, numPoints - 0.5], // Integer range with padding
        };

        // Flag for custom axis
        processedPlot.use_custom_x_axis = true;

        // FIX Y-AXIS RANGE BASED ON ACTUAL DATA VALUES
        // Collect all y values to determine proper range
        const allYValues: any = [];

        // Extract all y values from the lines
        processedPlot.lines.forEach((line: any) => {
          if (line.source && line.source.y && Array.isArray(line.source.y)) {
            // Filter out any non-numeric or invalid values
            const validYValues = line.source.y.filter(
              (val: any) => val !== undefined && val !== null && !isNaN(parseFloat(val))
            );

            // Convert strings to numbers if needed
            const numericYValues = validYValues.map((val: any) => (typeof val === 'string' ? parseFloat(val) : val));

            allYValues.push(...numericYValues);
          }
        });

        // Only process if we have valid y values
        if (allYValues.length > 0) {
          // Find min and max y values
          const minY = Math.min(...allYValues);
          const maxY = Math.max(...allYValues);

          // Add padding (20% on top and bottom)
          const yRange = Math.max(0.001, maxY - minY); // Ensure non-zero range
          const padding = yRange * 0.2;
          const yMin = minY - padding;
          const yMax = maxY + padding;

          // Update main y-axis range
          if (processedPlot.y_range) {
            processedPlot.y_range.plot_range = [yMin, yMax];
          } else {
            processedPlot.y_range = {
              bounds: ['auto'],
              plot_range: [yMin, yMax],
            };
          }

          // Also update any extra y ranges if they exist
          if (processedPlot.extra_y_ranges) {
            Object.keys(processedPlot.extra_y_ranges).forEach((key) => {
              const range = processedPlot.extra_y_ranges[key];
              if (range && range.plot_range) {
                range.plot_range = [yMin, yMax];
              }
            });
          }
        }
      } catch (error) {
        console.error('Error processing plot data:', error);
        return processedPlot;
      }
    }

    return processedPlot;
  }
}
